import { isAddress } from '../utils'

/**
 * Imágenes FAURT (fallback) para tokens sin arte propio del proyecto.
 * `dontpanic-alert.png` — señal "DON'T PANIC" del universo Hitchhiker.
 */
export const DEFAULT_TOKEN_ICON_URL = `${import.meta.env.BASE_URL}images/dontpanic-alert.png`
export const DEFAULT_TOKEN_BANNER_URL = `${import.meta.env.BASE_URL}images/dontpanic-alert.png`

/**
 * Disco oscuro detrás de iconos redondos (PNG/SVG con transparencia).
 * Alineado al mark del detalle de token (`--mobile-surface`).
 */
export const TOKEN_ICON_DISK_BACKGROUND = 'var(--mobile-surface, #0f1219)'

/** Extensiones admitidas para logos empaquetados en `public/images/suggested-tokens/`. */
export const BUNDLED_TOKEN_LOGO_EXTENSIONS = ['svg', 'png', 'webp', 'jpg', 'jpeg'] as const

/**
 * Variantes de nombre de archivo por dirección (EIP-55 y minúsculas).
 * Los FS de deploy suelen ser case-sensitive; el `<img>` prueba ambas.
 */
export function bundledTokenLogoAddressVariants(address: string): string[] {
  const checksummed = isAddress(address)
  const variants: string[] = []
  if (checksummed) variants.push(checksummed)
  const lower = (checksummed || address).toLowerCase()
  if (!variants.some((v) => v === lower)) variants.push(lower)
  if (!checksummed && address && !variants.includes(address)) variants.push(address)
  return variants
}

function bundledTokenLogoBasePath(fileAddress: string): string {
  return `${import.meta.env.BASE_URL}images/suggested-tokens/${fileAddress}`
}

/** Candidatos locales por dirección × extensión, hasta que `<img>` cargue uno. */
export function getBundledTokenLogoUrlCandidates(address: string): string[] {
  const urls: string[] = []
  for (const fileAddress of bundledTokenLogoAddressVariants(address)) {
    const base = bundledTokenLogoBasePath(fileAddress)
    for (const ext of BUNDLED_TOKEN_LOGO_EXTENSIONS) {
      urls.push(`${base}.${ext}`)
    }
  }
  return [...new Set(urls)]
}

/** URL preferida empaquetada (checksum + SVG cuando aplica). */
export function getBundledTokenLogoUrl(address: string): string {
  return getBundledTokenLogoUrlCandidates(address)[0]!
}

/** Candidatos de icono: arte empaquetado (casing + extensiones) → URL remota → FAURT. */
export function getTokenIconSrcCandidates(address: string, explicitUrl?: string | null): string[] {
  const candidates = [
    ...getBundledTokenLogoUrlCandidates(address),
    explicitUrl?.trim() || null,
    DEFAULT_TOKEN_ICON_URL,
  ].filter((value): value is string => Boolean(value))

  return [...new Set(candidates)]
}

/** Candidatos de portada: URL explícita → FAURT. */
export function getTokenBannerSrcCandidates(explicitUrl?: string | null): string[] {
  const candidates = [explicitUrl?.trim() || null, DEFAULT_TOKEN_BANNER_URL].filter(
    (value): value is string => Boolean(value),
  )

  return [...new Set(candidates)]
}

/** Icono resuelto para perfiles (prioriza arte empaquetado). */
export function resolveTokenProfileIconUrl(address: string): string {
  return getBundledTokenLogoUrl(address)
}

/** Portada resuelta para perfiles sin imagen curada local. */
export function resolveTokenProfileBannerUrl(): string {
  return DEFAULT_TOKEN_BANNER_URL
}
